<template>
  <Box>
    <form class="dialog" onsubmit="return false">
      <div class="header">
        <span>Create your account</span>
        <small>New accounts are reviewed by our team before access is granted.</small>
      </div>

      <div class="body">
        <form @submit.prevent="handleSubmit">
          <div class="form-row">
            <div class="field half">
              <label class="label">First Name <span style="color: red">*</span></label>
              <div class="control">
                <input v-model="formData.firstName" class="input" type="text" placeholder="First Name" required :disabled="isSaving" />
              </div>
            </div>
            <div class="field half">
              <label class="label">Last Name <span style="color: red">*</span></label>
              <div class="control">
                <input v-model="formData.lastName" class="input" type="text" placeholder="Last Name" required :disabled="isSaving" />
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="field half">
              <label class="label">Email <span style="color: red">*</span></label>
              <div class="control">
                <input v-model="formData.email" class="input" type="email" placeholder="Email" required :disabled="isSaving" />
              </div>
            </div>
            <div class="field half">
              <label class="label">Title <span style="color: red">*</span></label>
              <div class="control">
                <input v-model="formData.title" class="input" type="text" placeholder="Title" required :disabled="isSaving" />
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="field half">
              <label class="label">Phone <span style="color: red">*</span></label>
              <div class="control">
                <input v-model="formData.phoneNumber" class="input" type="tel" placeholder="(555) 555-5555" v-maska="'(###) ###-####'" required :disabled="isSaving" />
              </div>
            </div>
            <div class="field half">
              <label class="label">Extension</label>
              <div class="control">
                <input v-model="formData.extension" class="input" type="text" placeholder="Ext." :disabled="isSaving" />
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="field half">
              <label class="label">Affiliated Institution <span style="color: red">*</span></label>
              <div class="control">
                <multiselect
                  style="height: 45px !important"
                  v-model="formData.affiliatedInstitution"
                  :options="institutionOptions"
                  :multiple="false"
                  :searchable="true"
                  :internal-search="false"
                  :loading="isSearchingInstitutions"
                  :taggable="true"
                  :show-no-results="true"
                  tag-placeholder="Press enter to use this institution name"
                  placeholder="Search or add your institution"
                  label="label"
                  track-by="value"
                  :disabled="isSaving"
                  @search-change="handleInstitutionSearch"
                  @tag="handleAddInstitutionTag"
                ></multiselect>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">Anything else?</label>
            <div class="control">
              <textarea rows="3" v-model="formData.heardAboutUs" class="textarea" placeholder="Where did you hear about us?" :disabled="isSaving"></textarea>
            </div>
          </div>

          <div class="actions">
            <button type="button" class="button" style="margin-right: 10px" @click="() => closeDialog(null)" :disabled="isSaving">Cancel</button>
            &nbsp;&nbsp;
            <button class="button has-bcc-background has-text-white" style="width: 200px" type="submit" :disabled="isSaving">
              <spin-loader v-if="isSaving" />
              <span v-else>Create Account</span>
            </button>
          </div>
        </form>
      </div>
    </form>
  </Box>
</template>

<script setup>
import { onBeforeMount, ref } from 'vue'
import Box from './Box.vue'
import { closeDialog } from 'vue3-promise-dialog'
import { useGlobalUtils } from '../../helpers/globalUtils'

const { errorToast, successToast } = useGlobalUtils()

const isSaving = ref(false)
const formData = ref({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  title: '',
  affiliatedInstitution: null,
})

// full dataset fetched once on mount; institutionOptions is the filtered view shown to the user
const allInstitutionOptions = ref([])
const institutionOptions = ref([])
const isSearchingInstitutions = ref(false)

const handleInstitutionSearch = (query) => {
  if (!query) {
    institutionOptions.value = allInstitutionOptions.value
    return
  }
  const search = query.toLowerCase()
  institutionOptions.value = allInstitutionOptions.value.filter((option) => option.label.toLowerCase().includes(search))
}

const handleGetInstitutions = async () => {
  isSearchingInstitutions.value = true
  try {
    const req = await fetch(`${process.env.VUE_APP_API_URL}/institutions/new-account-request-institutions`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })

    if (!req.ok) throw new Error('An error occurred while searching for institutions.')

    const results = await req.json()
    allInstitutionOptions.value = (results || []).map((i) => ({
      label: `${i.bankName} - ${i.city}, ${i.state} - FDIC: ${i.fdic || 'N/A'}`,
      value: {
        institutionName: i.bankName,
        institutionFdic: i.fdic || 'N/A',
        institutionCity: i.city,
        institutionState: i.state,
        institutionSfid: i.sfid || 'N/A',
      },
    }))
    institutionOptions.value = allInstitutionOptions.value
  } catch (error) {
    errorToast('An error occurred while searching for institutions.')
  } finally {
    isSearchingInstitutions.value = false
  }
}

const handleAddInstitutionTag = (name) => {
  const tag = { label: name, value: { institutionName: name, institutionFdic: '', institutionCity: '', institutionState: '', institutionSfid: '' } }
  allInstitutionOptions.value.push(tag)
  institutionOptions.value.push(tag)
  formData.value.affiliatedInstitution = tag
}

const handleSubmit = async () => {
  if (!formData.value.affiliatedInstitution) {
    errorToast('Please select or enter an affiliated institution.')
    return
  }

  isSaving.value = true
  try {
    const req = await fetch(`${process.env.VUE_APP_API_URL}/users/new-account-request`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(formData.value),
    })

    if (req.status === 201) {
      successToast('Your account request has been submitted successfully! Our team will review it and get back to you shortly.')
      closeDialog(null)
    } else {
      const errorData = await req.json()
      throw new Error(errorData.message)
    }
  } catch (error) {
    errorToast(error || 'An error occurred while creating the account. Please try again later.')
  } finally {
    isSaving.value = false
  }
}

onBeforeMount(async () => {
  await handleGetInstitutions()
})
</script>

<style scoped lang="scss">
@import './dialog.scss';
@import '@/../node_modules/vue-multiselect/dist/vue-multiselect.min.css';

.dialog {
  width: 750px;
  max-width: 90vw;
  min-width: 300px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;

  .header {
    height: 100px;
    background-color: #fff;
    padding: 20px;
    font-size: 1.5rem;
    font-weight: bold;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    > span {
      font-size: 1.2rem;
      font-weight: bold;
    }

    > small {
      font-size: 0.8rem;
      font-weight: normal;
    }
  }
}

.form-row {
  display: flex;
  gap: 10px;

  .field {
    margin-bottom: 0.75rem;

    &.half {
      flex: 1;
    }

    &.two-thirds {
      flex: 2;
    }

    &.one-third {
      flex: 1;
    }
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
}
</style>
